import React from 'react';
import type { CombatStatistics, PlayerId } from '@drawn-of-war/shared';
import styles from './VictoryOverlay.module.css';

interface StatisticsDisplayProps {
  statistics: CombatStatistics;
  currentPlayerId: PlayerId;
}

// Format number with commas
function formatNumber(num: number): string {
  return num.toLocaleString();
}

// Format ticks to "M:SS"
function formatDuration(ticks: number): string {
  const seconds = Math.floor(ticks / 60);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Get opponent player ID
function getOpponentId(playerId: PlayerId): PlayerId {
  return playerId === 'player1' ? 'player2' : 'player1';
}

interface StatRowProps {
  label: string;
  currentPlayerValue: number;
  opponentValue: number;
  delay: number;
}

const StatRow: React.FC<StatRowProps> = ({
  label,
  currentPlayerValue,
  opponentValue,
  delay,
}) => {
  return (
    <div
      className={styles.statRow}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={styles.statLabel}>{label}</div>
      <div className={`${styles.statValue} ${styles.highlighted}`}>
        {formatNumber(currentPlayerValue)}
      </div>
      <div className={styles.statValue}>{formatNumber(opponentValue)}</div>
    </div>
  );
};

const StatisticsDisplay: React.FC<StatisticsDisplayProps> = ({
  statistics,
  currentPlayerId,
}) => {
  const opponentId = getOpponentId(currentPlayerId);

  return (
    <div className={styles.statisticsDisplay}>
      <div className={styles.statsHeader}>
        <div className={styles.headerLabel}></div>
        <div className={`${styles.headerPlayer} ${styles.highlighted}`}>YOU</div>
        <div className={styles.headerPlayer}>OPPONENT</div>
      </div>

      <div className={styles.statsGrid}>
        <StatRow
          label="Damage Dealt"
          currentPlayerValue={statistics.totalDamageDealt[currentPlayerId]}
          opponentValue={statistics.totalDamageDealt[opponentId]}
          delay={0}
        />
        <StatRow
          label="Healing Done"
          currentPlayerValue={statistics.totalHealingDone[currentPlayerId]}
          opponentValue={statistics.totalHealingDone[opponentId]}
          delay={100}
        />
        <StatRow
          label="Units Lost"
          currentPlayerValue={statistics.unitsLost[currentPlayerId]}
          opponentValue={statistics.unitsLost[opponentId]}
          delay={200}
        />
        <StatRow
          label="Abilities Used"
          currentPlayerValue={statistics.abilitiesUsed[currentPlayerId]}
          opponentValue={statistics.abilitiesUsed[opponentId]}
          delay={300}
        />
      </div>

      <div className={styles.durationDisplay}>
        <span className={styles.durationLabel}>Battle Duration:</span>{' '}
        <span className={styles.durationValue}>{formatDuration(statistics.duration)}</span>
      </div>
    </div>
  );
};

export default StatisticsDisplay;
